import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import useLocales from '../../../hooks/useLocales';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RootState, useDispatch, useSelector } from '../../../redux/store';
import { selectSteelCategory } from '../../../redux/slices/millSheetSearch';
import getSteelCategoryInSearchParams from '../../../utils/mill-sheet-search/getSteelCategoryInSearchParams';
import getOptionsTypeLabel from '../../../utils/getOptionsTypeLabel';
import type { SteelCategory } from '../../../types/common';
import type { OptionsType } from '../../../types/options';

/**
 * 分類のカテゴリ選択プルダウンから分類を選んだらstateにそれを保存する（検索はかけない）。
 * OptionsTypeを選択肢として持つ。
 * @returns [React.ReactElement]
 */
const SelectSteelCategory = (): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentLang } = useLocales();
  const { steelCategoryType } = useSelector((state: RootState) => state.options);
  const [selectedOption, setSelectedOption] = useState<OptionsType<SteelCategory | ''> | null>(null);
  const getOptionLabel = (option: OptionsType) => getOptionsTypeLabel(option, currentLang.value);
  const getOptionValue = (option: OptionsType) => String(option.code);

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  // URLが変わったら必要に応じて選択状態を変更する
  useEffect(() => {
    if (steelCategoryType.length > 0) {
      const params = new URLSearchParams(location.search);
      const q = decodeURIComponent(params.get('q'));
      const steelCategoryInSearchParams: SteelCategory | undefined = getSteelCategoryInSearchParams(q, steelCategoryType);
      // console.log(steelCategoryInSearchParams);
      const selected: OptionsType<SteelCategory> | undefined = steelCategoryType.find((option) => option.code === steelCategoryInSearchParams);
      if (selected) {
        setSelectedOption(selected);
      } else {
        setSelectedOption(null);
      }
      const selectedSteelCategory: SteelCategory | undefined = selected ? selected.code : undefined;
      dispatch(selectSteelCategory(selectedSteelCategory));
    }
  }, [steelCategoryType, location.search]);

  // EVENT HANDLER
  // ================================================================================
  /**
   * プルダウンから分類を選んだとき
   */
  // const handleSteelCategoryChange = (option: SelectOption<SteelCategory> | null): void => {
  const handleSteelCategoryChange = (option: OptionsType<SteelCategory> | null): void => {
    // console.log(option?.code);
    setSelectedOption(option);
    const selectedSteelCategory: SteelCategory | undefined = option ? option.code : undefined;
    dispatch(selectSteelCategory(selectedSteelCategory));
    // onChange(option);
    // // 選択された選択肢から品種のクエリを作成
    // const selectedSteelCategory: SteelCategory | undefined = option?.value;
    // const queryBarWireRodJa: string = '品種:(線材 OR 棒鋼)';
    // const querySheetSteelJa: string = '品種:(厚板 OR 薄板)';
    // const queryPipeJa: string = '品種:鋼管';
    // const queryBarWireRodEn: string = '品種_EN:(BAR OR WIRE_ROD)';
    // const querySheetSteelEn: string = '品種_EN:(STEEL_PLATE OR STEEL_SHEET)';
    // const queryPipeEn: string = '品種_EN:PIPE';
    // // 条件に当てはまる場合はクエリを、当てはまらない場合（分類選択をやめた場合）は空文字列を返す
    // const steelCategoryQuery: string = (() => {
    //   if (currentLang.value === LANGUAGE.JA) {
    //     if (selectedSteelCategory === STEEL_CATEGORY.BAR) {
    //       return queryBarWireRodJa;
    //     } else if (selectedSteelCategory === STEEL_CATEGORY.PLATE) {
    //       return querySheetSteelJa;
    //     } else if (selectedSteelCategory === STEEL_CATEGORY.PIPE) {
    //       return queryPipeJa;
    //     } else {
    //       return '';
    //     }
    //   } else if (currentLang.value === LANGUAGE.EN) {
    //     if (selectedSteelCategory === STEEL_CATEGORY.BAR) {
    //       return queryBarWireRodEn;
    //     } else if (selectedSteelCategory === STEEL_CATEGORY.PLATE) {
    //       return querySheetSteelEn;
    //     } else if (selectedSteelCategory === STEEL_CATEGORY.PIPE) {
    //       return queryPipeEn;
    //     } else {
    //       return '';
    //     }
    //   } else {
    //     return '';
    //   }
    // })();
    // console.log(steelCategoryQuery);
    // const params = new URLSearchParams(location.search);
    // const q = decodeURIComponent(params.get('q'));
    // const queryItems: string[] = q !== 'null' ? q.split(' AND ') : [];
    // // console.log(queryItems);
    // // 品種・品種_ENのクエリを取り除いておく
    // const exceptSteelCategory: string[] = queryItems.filter((query) => {
    //   // console.log(query);
    //   if (query.indexOf('品種:') === -1 && query.indexOf('品種_EN:') === -1) {
    //     return true;
    //   } else {
    //     return false;
    //   }
    // });
    // console.log(exceptSteelCategory);
    // // 分類クエリが空文字列でなかった場合は整形したクエリを、空文字列だった場合は元のクエリを返す
    // const newSearchParam: string = steelCategoryQuery !== '' ? [steelCategoryQuery, ...exceptSteelCategory].join(' AND ') : exceptSteelCategory.join(' AND ');
    // // URLを変更する
    // params.set('q', encodeURIComponent(newSearchParam));
    // history.push({ search: params.toString() });
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div id="selectSteelCategoryContainer" className="w-100 d-flex align-items-center">
        <label className="mb-0" htmlFor="selectSteelCategory">
          {t('steelCategory')}：
        </label>
        <div className="flex-fill mr-4">
          <Select
            inputId={'selectSteelCategory'}
            name={'selectSteelCategory'}
            value={selectedOption}
            isClearable={true}
            options={steelCategoryType}
            getOptionLabel={getOptionLabel}
            getOptionValue={getOptionValue}
            onChange={handleSteelCategoryChange}
            placeholder={t('dropdown.not')}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
      </div>
    </>
  );
};

export default SelectSteelCategory;
